// US keyword data — the 15 high-intent "[Location] [Keyword]" page types.
// Combined with usLocations.js to generate /us/:location/:keyword pages.
//
// Copy uses placeholders that the template fills in:
//   {loc}   → location name (e.g. "California", "Austin")
//   {state} → parent state name (falls back to {loc})
//
// Each entry:
//   slug, name, shortName, emoji
//   serviceHref   the existing Lumotis page this keyword maps to
//   heroAccent    gradient sub-headline
//   tagline       hero paragraph
//   stats[3]      { value, label }
//   pillars[6]    { title, body }
//   faq[4]        { q, a }
//   related[3]    related keyword slugs

export const US_KEYWORDS = [
  {
    slug: "digital-marketing-agency",
    name: "Digital Marketing Agency",
    shortName: "Digital Marketing",
    emoji: "🚀",
    serviceHref: "/us",
    heroAccent: "Every channel. One growth engine.",
    tagline:
      "Lumotis is a full-stack digital marketing agency serving {loc} businesses — SEO, Google & Meta Ads, social, email and AI search, all coordinated under one roof to turn traffic into revenue.",
    stats: [
      { value: "3.2×", label: "avg revenue growth" },
      { value: "150+", label: "businesses scaled" },
      { value: "24/7", label: "campaign monitoring" },
    ],
    pillars: [
      { title: "SEO & AI Search Visibility", body: "Rank in Google and get cited by ChatGPT, Gemini and AI Overviews for the searches {loc} customers actually make." },
      { title: "Google & Meta Ads", body: "Profit-focused paid campaigns targeted to {loc} audiences, with creative, landing pages and bidding managed in-house." },
      { title: "Social Media Growth", body: "Content, community and paid social that builds a real audience for your brand across {loc}." },
      { title: "Email & SMS Marketing", body: "Automated flows and campaigns that turn one-time buyers into repeat revenue." },
      { title: "Web Design & CRO", body: "Conversion-optimised websites and landing pages built to turn {loc} traffic into leads and sales." },
      { title: "Analytics & Reporting", body: "One dashboard tying every channel to revenue — so you always know what's working." },
    ],
    faq: [
      { q: "Do you work with businesses across all of {loc}?", a: "Yes. We serve businesses throughout {loc} — from local service companies to ecommerce brands and B2B firms — with strategies localised to your specific area and audience." },
      { q: "What makes Lumotis different from other {loc} digital marketing agencies?", a: "We run every channel under one roof and tie everything to revenue, not vanity metrics. No juggling five vendors — one senior team, one strategy, one number that matters." },
      { q: "How quickly will we see results?", a: "Paid ads typically show results within 2–4 weeks. SEO and AI search compound over 3–6 months. We prioritise quick wins first while building long-term growth." },
      { q: "What does it cost to hire a digital marketing agency in {loc}?", a: "Engagements are scoped to your goals and start with a free audit. Most {loc} clients invest a monthly retainer plus ad spend, with clear ROI targets agreed up front." },
    ],
    related: ["seo-agency", "ppc-agency", "social-media-marketing-agency"],
  },

  {
    slug: "seo-agency",
    name: "SEO Agency",
    shortName: "SEO",
    emoji: "🔍",
    serviceHref: "/seo",
    heroAccent: "Rank higher. Own the searches that matter.",
    tagline:
      "Lumotis is a results-driven SEO agency helping {loc} businesses rank on page one, win AI search citations and turn organic traffic into a compounding revenue channel.",
    stats: [
      { value: "+210%", label: "avg organic traffic lift" },
      { value: "Top 3", label: "rankings for target keywords" },
      { value: "6 mo", label: "to compounding growth" },
    ],
    pillars: [
      { title: "Keyword & Competitor Research", body: "We map exactly what {loc} customers search for and where competitors are weak." },
      { title: "Technical SEO", body: "Site speed, Core Web Vitals, crawlability, schema and architecture — the foundation Google rewards." },
      { title: "On-Page Optimisation", body: "Titles, content, internal linking and intent-matched pages built to rank and convert." },
      { title: "Content Strategy", body: "Authority content that earns rankings, links and AI citations for {loc} searches." },
      { title: "Link Building & Digital PR", body: "High-authority backlinks that move rankings — no spam, no shortcuts." },
      { title: "AI Search (GEO/AEO)", body: "Structure your content so ChatGPT, Perplexity and Google AI Overviews recommend you." },
    ],
    faq: [
      { q: "How long does SEO take to work in {loc}?", a: "Most clients see meaningful ranking and traffic improvements within 3–6 months, with results compounding after that. Competitive {loc} keywords may take longer; quick wins often land sooner." },
      { q: "Do you guarantee #1 rankings?", a: "No honest SEO agency can guarantee rankings — Google controls that. We guarantee a proven process, full transparency and a relentless focus on the traffic that drives revenue." },
      { q: "Is SEO still worth it with AI search?", a: "More than ever. AI engines cite well-optimised, authoritative content. Our SEO includes GEO/AEO so you show up in Google *and* in AI answers." },
      { q: "What's included in your {loc} SEO service?", a: "Technical audits, keyword strategy, on-page optimisation, content, link building, local SEO where relevant and monthly reporting tied to leads and revenue." },
    ],
    related: ["local-seo-company", "content-marketing-agency", "ai-marketing-agency"],
  },

  {
    slug: "ecommerce-marketing-agency",
    name: "Ecommerce Marketing Agency",
    shortName: "Ecommerce Marketing",
    emoji: "🛒",
    serviceHref: "/shopify-ecommerce",
    heroAccent: "More revenue. Lower CAC. Compounding growth.",
    tagline:
      "Lumotis is an ecommerce marketing agency that scales {loc} online stores with Google Shopping, Meta Ads, Klaviyo email, SEO and AI search — the complete revenue stack under one roof.",
    stats: [
      { value: "+340%", label: "avg ROAS increase" },
      { value: "3.2×", label: "avg store revenue growth" },
      { value: "50+", label: "stores scaled" },
    ],
    pillars: [
      { title: "Google Shopping & Performance Max", body: "Margin-based campaign structure and feed optimisation that scales profit, not just revenue." },
      { title: "Meta Ads & UGC Creative", body: "Prospecting and retargeting with scroll-stopping creative produced in-house for {loc} audiences." },
      { title: "Klaviyo Email & SMS Flows", body: "Abandoned cart, post-purchase, win-back and VIP flows — often 30–45% of store revenue." },
      { title: "Ecommerce SEO", body: "Collection and product page optimisation that builds free, compounding traffic." },
      { title: "Conversion Rate Optimisation", body: "PDP, cart and checkout testing so traffic converts before you scale spend." },
      { title: "AI Search & Marketplace Visibility", body: "Get your products recommended by AI shopping assistants and Google AI Overviews." },
    ],
    faq: [
      { q: "Which ecommerce platforms do you work with?", a: "Shopify is our most common platform, but we regularly work with WooCommerce, BigCommerce and custom stores. The marketing stack is platform-agnostic." },
      { q: "What ad budget do {loc} stores need to see results?", a: "We recommend at least $3,000–$5,000/month in ad spend so campaigns learn quickly and produce meaningful ROAS data." },
      { q: "Do you handle creative and product photography?", a: "Yes — our in-house team produces static, video and UGC-style creative, and we brief and test everything." },
      { q: "How do you measure ecommerce success?", a: "Blended ROAS, customer acquisition cost, LTV and total store revenue — the metrics that actually determine profit for {loc} brands." },
    ],
    related: ["shopify-marketing-agency", "ppc-agency", "email-marketing-agency"],
  },

  {
    slug: "ppc-agency",
    name: "PPC & Google Ads Agency",
    shortName: "PPC & Google Ads",
    emoji: "🎯",
    serviceHref: "/paid-advertising",
    heroAccent: "Profitable paid traffic, on demand.",
    tagline:
      "Lumotis is a PPC agency running profit-focused Google Ads, Microsoft Ads and paid search campaigns for {loc} businesses — with conversion tracking, landing pages and bidding managed in-house.",
    stats: [
      { value: "−38%", label: "avg cost per lead" },
      { value: "+2.7×", label: "avg ROAS improvement" },
      { value: "$10M+", label: "ad spend managed" },
    ],
    pillars: [
      { title: "Google Search Ads", body: "Intent-based campaigns capturing {loc} customers at the exact moment they search." },
      { title: "Performance Max & Shopping", body: "AI-driven campaigns structured for profit across Search, Display, YouTube and Shopping." },
      { title: "Landing Page Optimisation", body: "Dedicated, fast landing pages built to convert paid clicks into leads and sales." },
      { title: "Conversion Tracking & Attribution", body: "Accurate tracking so every dollar is tied to a real business outcome." },
      { title: "Retargeting & Remarketing", body: "Bring back the 97% of visitors who don't convert on their first visit." },
      { title: "Continuous Optimisation", body: "Weekly bid, keyword, negative and creative refinement — never set-and-forget." },
    ],
    faq: [
      { q: "How much should a {loc} business spend on Google Ads?", a: "It depends on your market and goals, but most clients start at $2,000–$5,000/month. We recommend a budget that lets campaigns gather enough data to optimise quickly." },
      { q: "How fast will PPC generate leads?", a: "Campaigns can generate leads within days of launch. Expect 2–4 weeks of optimisation before cost-per-lead stabilises at its best." },
      { q: "Do you charge a percentage of ad spend?", a: "We offer transparent flat-fee or tiered management pricing — so we're incentivised by your results, not by spending more." },
      { q: "Can you take over an existing Google Ads account?", a: "Yes. We audit your existing account first, preserve what works and rebuild what doesn't — usually cutting wasted spend significantly." },
    ],
    related: ["facebook-ads-agency", "cro-agency", "digital-marketing-agency"],
  },

  {
    slug: "social-media-marketing-agency",
    name: "Social Media Marketing Agency",
    shortName: "Social Media",
    emoji: "📱",
    serviceHref: "/social-media",
    heroAccent: "Content that builds audiences and drives sales.",
    tagline:
      "Lumotis is a social media marketing agency helping {loc} brands grow on Instagram, TikTok, LinkedIn and Facebook — with strategy, content production, community management and paid social.",
    stats: [
      { value: "+180%", label: "avg engagement growth" },
      { value: "4.1×", label: "avg follower growth" },
      { value: "30+", label: "posts produced monthly" },
    ],
    pillars: [
      { title: "Platform Strategy", body: "The right channels, cadence and content pillars for your {loc} audience." },
      { title: "Content Production", body: "Short-form video, graphics and copy produced in-house, on-brand and on-trend." },
      { title: "Community Management", body: "Daily engagement, DMs and comments handled so your brand feels alive and responsive." },
      { title: "Paid Social", body: "Meta, TikTok and LinkedIn ads that turn attention into leads and sales." },
      { title: "Influencer & Creator Partnerships", body: "Local {loc} creators and micro-influencers who drive authentic reach." },
      { title: "Analytics & Growth Reporting", body: "Clear reporting on reach, engagement and — most importantly — revenue." },
    ],
    faq: [
      { q: "Which social platforms should a {loc} business focus on?", a: "It depends on your audience. B2C brands usually win on Instagram and TikTok; B2B on LinkedIn. We recommend focusing on 2–3 platforms and doing them exceptionally well." },
      { q: "Do you create the content or just schedule it?", a: "We create it — strategy, filming, editing, design and copy — then schedule, publish and engage." },
      { q: "How long until social media drives revenue?", a: "Paid social can drive sales within weeks. Organic audience growth typically takes 3–6 months to build real momentum." },
      { q: "Do you work with local {loc} influencers?", a: "Yes — we source and manage creator partnerships, including local micro-influencers who resonate with {loc} audiences." },
    ],
    related: ["facebook-ads-agency", "content-marketing-agency", "branding-agency"],
  },

  {
    slug: "web-design-agency",
    name: "Web Design Agency",
    shortName: "Web Design",
    emoji: "🎨",
    serviceHref: "/web-design",
    heroAccent: "Beautiful websites engineered to convert.",
    tagline:
      "Lumotis is a web design agency building fast, conversion-focused websites for {loc} businesses — on Webflow, Shopify and WordPress — designed to rank, load instantly and turn visitors into customers.",
    stats: [
      { value: "+64%", label: "avg conversion lift post-launch" },
      { value: "<2s", label: "load time target" },
      { value: "100+", label: "sites launched" },
    ],
    pillars: [
      { title: "UX & Conversion-Led Design", body: "Every page designed around what makes {loc} visitors take action." },
      { title: "Custom Development", body: "Webflow, Shopify and WordPress builds — clean, fast and easy for your team to manage." },
      { title: "Speed & Core Web Vitals", body: "Performance-first engineering so your site ranks and converts on mobile." },
      { title: "SEO-Ready Architecture", body: "Structure, schema and content built in from day one — not bolted on later." },
      { title: "Brand & Visual Identity", body: "Design that reflects your brand and stands out in the {loc} market." },
      { title: "Ongoing Optimisation", body: "Post-launch A/B testing and iteration to keep improving conversion." },
    ],
    faq: [
      { q: "How much does a website cost for a {loc} business?", a: "Projects are scoped to your needs — from focused landing pages to full ecommerce builds. We provide a clear fixed quote after a discovery call." },
      { q: "How long does a website take to build?", a: "Most projects launch in 4–8 weeks depending on scope. We work in sprints with regular reviews so there are no surprises." },
      { q: "Will my website be optimised for search?", a: "Yes — SEO is built into the architecture, speed, content structure and schema from the start." },
      { q: "Can you redesign my existing website?", a: "Absolutely. We audit what's working, preserve your SEO equity and rebuild for speed, clarity and conversion." },
    ],
    related: ["cro-agency", "branding-agency", "seo-agency"],
  },

  {
    slug: "content-marketing-agency",
    name: "Content Marketing Agency",
    shortName: "Content Marketing",
    emoji: "✍️",
    serviceHref: "/content-marketing",
    heroAccent: "Content that ranks, converts and compounds.",
    tagline:
      "Lumotis is a content marketing agency creating SEO content, blogs, lead magnets and thought leadership that attract {loc} customers, earn AI citations and drive qualified leads.",
    stats: [
      { value: "+250%", label: "avg organic traffic from content" },
      { value: "3.5×", label: "more leads than paid alone" },
      { value: "Forever", label: "content keeps working" },
    ],
    pillars: [
      { title: "Content Strategy", body: "A keyword- and intent-mapped plan aligned to how {loc} buyers research and decide." },
      { title: "SEO Blog & Article Writing", body: "Expert-level content that ranks in Google and gets cited by AI engines." },
      { title: "Lead Magnets & Guides", body: "Ebooks, checklists and tools that turn readers into leads." },
      { title: "Thought Leadership", body: "Authority content that positions your brand as the go-to expert in {loc}." },
      { title: "Video & Multimedia", body: "Repurposed content for YouTube, social and email — one idea, many formats." },
      { title: "Distribution & Promotion", body: "Content that actually gets seen via email, social, outreach and PR." },
    ],
    faq: [
      { q: "How much content does a {loc} business need?", a: "Quality beats volume. Most clients publish 4–8 high-value pieces per month, strategically targeted to the searches that drive revenue." },
      { q: "Do you write the content yourselves?", a: "Yes — our writers and strategists create it, with subject-matter input from you. AI assists research, but humans craft the final work." },
      { q: "How does content marketing generate leads?", a: "By ranking for the questions your customers ask, building trust and guiding them to conversion points — lead magnets, consultations and offers." },
      { q: "How long until content marketing shows results?", a: "Expect traction in 3–6 months, with content compounding in value over years — unlike paid ads that stop when spend stops." },
    ],
    related: ["seo-agency", "social-media-marketing-agency", "ai-marketing-agency"],
  },

  {
    slug: "local-seo-company",
    name: "Local SEO Company",
    shortName: "Local SEO",
    emoji: "📍",
    serviceHref: "/local-seo",
    heroAccent: "Dominate 'near me' searches in your area.",
    tagline:
      "Lumotis is a local SEO company helping {loc} businesses rank in the Google Map Pack, win 'near me' searches and turn local search into a steady flow of calls, visits and bookings.",
    stats: [
      { value: "Top 3", label: "Map Pack placement target" },
      { value: "+165%", label: "avg calls from Google" },
      { value: "4.8★", label: "avg client rating achieved" },
    ],
    pillars: [
      { title: "Google Business Profile Optimisation", body: "Fully optimised profile that ranks in the Map Pack for {loc} searches." },
      { title: "Local Keyword Strategy", body: "Target the exact 'near me' and neighbourhood searches your customers use." },
      { title: "Review Generation & Management", body: "Systems that consistently earn 5-star reviews and respond to every one." },
      { title: "Local Citations & Listings", body: "Consistent NAP data across directories that Google trusts." },
      { title: "Location & Service Pages", body: "Dedicated pages for every area and service you cover across {loc}." },
      { title: "Local Link Building", body: "Links from {loc} organisations, press and partners that boost local authority." },
    ],
    faq: [
      { q: "How long does local SEO take in {loc}?", a: "Google Business Profile improvements can show within weeks. Consistent Map Pack rankings for competitive {loc} searches typically take 3–6 months." },
      { q: "Do you help with Google reviews?", a: "Yes — we build review-generation systems and manage responses, which is one of the strongest local ranking and conversion factors." },
      { q: "Can I rank in multiple areas of {loc}?", a: "Yes. We build location pages and optimise your profile(s) to rank across the neighbourhoods and cities you serve." },
      { q: "Is local SEO right for my business?", a: "If customers visit you, you serve a specific area, or people search '[service] near me' for what you do — yes, it's one of the highest-ROI channels available." },
    ],
    related: ["seo-agency", "ppc-agency", "web-design-agency"],
  },

  {
    slug: "facebook-ads-agency",
    name: "Facebook & Meta Ads Agency",
    shortName: "Meta Ads",
    emoji: "📣",
    serviceHref: "/paid-advertising",
    heroAccent: "Scroll-stopping ads. Scalable results.",
    tagline:
      "Lumotis is a Facebook & Meta Ads agency running profitable Instagram and Facebook campaigns for {loc} businesses — creative production, audience strategy and scaling all handled in-house.",
    stats: [
      { value: "+3.1×", label: "avg ROAS" },
      { value: "−42%", label: "avg cost per acquisition" },
      { value: "500+", label: "ad creatives tested" },
    ],
    pillars: [
      { title: "Audience & Funnel Strategy", body: "Prospecting, retargeting and lookalike structure built for {loc} buyers." },
      { title: "Creative Production", body: "Static, video and UGC-style ads produced and tested in-house." },
      { title: "Conversion API & Tracking", body: "Accurate tracking so Meta's algorithm optimises on real purchases and leads." },
      { title: "Retargeting Sequences", body: "Multi-touch campaigns that bring back visitors and abandoned carts." },
      { title: "Scaling Playbooks", body: "Proven methods to scale winning campaigns without killing efficiency." },
      { title: "Weekly Testing & Reporting", body: "Constant creative and audience testing with transparent performance reporting." },
    ],
    faq: [
      { q: "Do Facebook ads still work for {loc} businesses?", a: "Yes — Meta remains one of the highest-performing channels when creative, tracking and audience strategy are done right. Most under-performance comes from weak creative or broken tracking." },
      { q: "What budget do I need for Meta ads?", a: "We recommend at least $1,500–$3,000/month to gather enough data for the algorithm to optimise effectively." },
      { q: "Do you make the ad creatives?", a: "Yes — our team produces all creative, including video and UGC-style content, and we test dozens of variations to find winners." },
      { q: "How is this different from boosting posts?", a: "Boosted posts lack proper targeting, funnel structure and tracking. Real campaigns use conversion objectives, retargeting and creative testing — a completely different level of performance." },
    ],
    related: ["ppc-agency", "social-media-marketing-agency", "ecommerce-marketing-agency"],
  },

  {
    slug: "email-marketing-agency",
    name: "Email Marketing Agency",
    shortName: "Email Marketing",
    emoji: "✉️",
    serviceHref: "/email-sms",
    heroAccent: "Turn your list into your most profitable channel.",
    tagline:
      "Lumotis is an email marketing agency building automated flows, campaigns and SMS programs for {loc} businesses on Klaviyo, HubSpot and Mailchimp — driving repeat revenue at near-zero cost per sale.",
    stats: [
      { value: "30–45%", label: "of revenue from email (ecommerce)" },
      { value: "36:1", label: "avg email ROI" },
      { value: "+58%", label: "avg open-rate improvement" },
    ],
    pillars: [
      { title: "Automated Flows", body: "Welcome, abandoned cart, post-purchase, win-back and VIP sequences that run 24/7." },
      { title: "Campaign Strategy & Calendar", body: "Regular sends planned around {loc} seasonality, launches and promotions." },
      { title: "Design & Copywriting", body: "On-brand templates and copy that get opened, read and clicked." },
      { title: "List Growth & Segmentation", body: "Capture more subscribers and message each segment relevantly." },
      { title: "SMS Marketing", body: "High-open-rate text campaigns and automations that complement email." },
      { title: "Deliverability & Testing", body: "Inbox placement, A/B testing and reporting tied to revenue." },
    ],
    faq: [
      { q: "Which email platforms do you work with?", a: "Klaviyo (ecommerce), HubSpot and ActiveCampaign (B2B/services), Mailchimp and others. We recommend the best fit for your {loc} business." },
      { q: "How much revenue can email generate?", a: "For ecommerce, well-built email programs typically drive 30–45% of total revenue. For service businesses, email consistently delivers the lowest cost per lead." },
      { q: "Do you write and design the emails?", a: "Yes — strategy, copy, design, build, testing and reporting are all handled by our team." },
      { q: "How fast do email flows start working?", a: "Core automated flows can be live within 2–4 weeks and begin generating revenue immediately from your existing traffic and list." },
    ],
    related: ["marketing-automation-agency", "ecommerce-marketing-agency", "cro-agency"],
  },

  {
    slug: "cro-agency",
    name: "Conversion Rate Optimization Agency",
    shortName: "CRO",
    emoji: "📈",
    serviceHref: "/cro",
    heroAccent: "Same traffic. 2–5× more revenue.",
    tagline:
      "Lumotis is a conversion rate optimization agency helping {loc} businesses turn more of their existing traffic into leads and sales — through research, A/B testing and data-driven design.",
    stats: [
      { value: "2–5×", label: "typical conversion lift" },
      { value: "+41%", label: "avg revenue per visitor" },
      { value: "100+", label: "tests run per year" },
    ],
    pillars: [
      { title: "Conversion Research", body: "Heatmaps, session recordings, surveys and analytics to find exactly where {loc} visitors drop off." },
      { title: "A/B & Multivariate Testing", body: "Statistically rigorous experiments on pages, offers, copy and layouts." },
      { title: "Landing Page Optimisation", body: "High-converting pages for paid traffic and key entry points." },
      { title: "Checkout & Funnel Optimisation", body: "Remove friction from cart, checkout and lead forms." },
      { title: "Personalisation", body: "Tailored experiences by source, segment and behaviour." },
      { title: "Analytics & Insights", body: "Clear reporting on what moved the needle and what to test next." },
    ],
    faq: [
      { q: "How much traffic do I need for CRO?", a: "For reliable A/B testing, roughly 10,000+ monthly visitors is ideal. Lower-traffic {loc} sites still benefit from research-driven improvements without formal testing." },
      { q: "What kind of conversion lift can we expect?", a: "Most clients see 20–60% improvements in the first few months, with some doubling or tripling conversion rates over a year of testing." },
      { q: "Does CRO work alongside our paid ads?", a: "It's the perfect pairing — every improvement in conversion rate directly lowers your cost per acquisition and boosts ROAS." },
      { q: "How long is a typical CRO engagement?", a: "CRO is an ongoing program — most clients run continuous testing for 6–12+ months as gains compound." },
    ],
    related: ["web-design-agency", "ppc-agency", "ecommerce-marketing-agency"],
  },

  {
    slug: "shopify-marketing-agency",
    name: "Shopify Marketing Agency",
    shortName: "Shopify Marketing",
    emoji: "🛍️",
    serviceHref: "/shopify-ecommerce",
    heroAccent: "Built for Shopify. Obsessed with revenue.",
    tagline:
      "Lumotis is a Shopify marketing agency scaling {loc} Shopify stores with Google Shopping, Meta Ads, Klaviyo flows, Shopify SEO and conversion optimisation — the complete growth stack for Shopify brands.",
    stats: [
      { value: "+340%", label: "avg ROAS increase" },
      { value: "38%", label: "avg revenue from email flows" },
      { value: "50+", label: "Shopify stores scaled" },
    ],
    pillars: [
      { title: "Google Shopping & Merchant Center", body: "Feed optimisation and Shopping campaigns structured for profit." },
      { title: "Meta & TikTok Ads", body: "UGC-style creative and full-funnel campaigns for {loc} shoppers." },
      { title: "Klaviyo Email & SMS", body: "The flow stack that typically drives 30–45% of Shopify revenue." },
      { title: "Shopify SEO", body: "Collection, product and blog optimisation for compounding organic sales." },
      { title: "Theme & Checkout CRO", body: "Speed, PDP and checkout optimisation using Shopify's best-practice patterns." },
      { title: "App Stack & Automation", body: "The right apps — reviews, upsells, subscriptions — configured for growth." },
    ],
    faq: [
      { q: "Do you only work with Shopify stores?", a: "Shopify is our specialty and most common platform, though we also support other ecommerce platforms. Our Shopify expertise means faster setup and proven playbooks." },
      { q: "Can you help with Shopify apps and theme setup?", a: "Yes — we audit and configure your app stack (reviews, upsells, subscriptions, email) and optimise your theme for speed and conversion." },
      { q: "What results do {loc} Shopify stores typically see?", a: "Clients commonly see ROAS improvements of 2–4×, email growing to 30%+ of revenue and meaningful reductions in customer acquisition cost within 90 days." },
      { q: "Do you offer a free Shopify store audit?", a: "Yes — use our free Shopify audit tool for an instant report on your store's conversion, marketing and SEO setup." },
    ],
    related: ["ecommerce-marketing-agency", "email-marketing-agency", "facebook-ads-agency"],
  },

  {
    slug: "marketing-automation-agency",
    name: "Marketing Automation Agency",
    shortName: "Marketing Automation",
    emoji: "⚙️",
    serviceHref: "/marketing-automation",
    heroAccent: "Systems that sell while you sleep.",
    tagline:
      "Lumotis is a marketing automation agency building HubSpot, Klaviyo, ActiveCampaign and GoHighLevel systems for {loc} businesses — automating lead nurture, follow-up and retention so no opportunity slips away.",
    stats: [
      { value: "−60%", label: "manual follow-up time" },
      { value: "+47%", label: "avg lead-to-customer rate" },
      { value: "24/7", label: "nurture running" },
    ],
    pillars: [
      { title: "Platform Setup & Migration", body: "HubSpot, Klaviyo, ActiveCampaign or GoHighLevel configured properly from day one." },
      { title: "Lead Nurture Workflows", body: "Automated sequences that warm up {loc} leads until they're ready to buy." },
      { title: "CRM & Pipeline Automation", body: "Lead scoring, routing and task automation so sales follows up at the right time." },
      { title: "Lifecycle & Retention Flows", body: "Onboarding, upsell, renewal and win-back automations that grow lifetime value." },
      { title: "Integrations & Data", body: "Connect your website, ads, CRM and tools so data flows automatically." },
      { title: "Reporting Dashboards", body: "One view of pipeline, revenue and automation performance." },
    ],
    faq: [
      { q: "Which automation platform is best for my {loc} business?", a: "It depends on your model: Klaviyo for ecommerce, HubSpot or ActiveCampaign for B2B and services, GoHighLevel for agencies and local businesses. We'll recommend the right fit." },
      { q: "Can you migrate us from our current platform?", a: "Yes — we handle full migrations including contacts, workflows, templates and integrations with zero data loss." },
      { q: "How long does setup take?", a: "Core automations are usually live in 3–6 weeks depending on complexity and integrations." },
      { q: "Will automation feel robotic to our customers?", a: "Not when it's done well. We design automations that feel personal and timely — the right message at the right moment, not spam." },
    ],
    related: ["email-marketing-agency", "ai-marketing-agency", "cro-agency"],
  },

  {
    slug: "ai-marketing-agency",
    name: "AI Marketing Agency",
    shortName: "AI Marketing",
    emoji: "🤖",
    serviceHref: "/ai-automation",
    heroAccent: "AI search visibility + AI automation, done for you.",
    tagline:
      "Lumotis is an AI marketing agency helping {loc} businesses get found in ChatGPT, Gemini and Google AI Overviews — and deploy AI automation for lead response, booking and customer support.",
    stats: [
      { value: "24/7", label: "AI lead response" },
      { value: "+47%", label: "avg lead conversion with AI" },
      { value: "Top", label: "AI search citations" },
    ],
    pillars: [
      { title: "AI Search Optimisation (GEO/AEO)", body: "Structure your content so AI engines recommend your {loc} business in their answers." },
      { title: "AI Lead-Response Agents", body: "Instant, qualified replies to every enquiry — day or night." },
      { title: "AI Booking & Scheduling", body: "24/7 appointment booking wired into your calendar and CRM." },
      { title: "AI Customer Support", body: "Trained support agents that resolve routine questions and escalate the rest." },
      { title: "AI-Powered Content & Ads", body: "Faster, data-driven creative and content production with human quality control." },
      { title: "Automation & Integration", body: "AI connected to your existing tools so it works within your workflows." },
    ],
    faq: [
      { q: "What is AI search optimisation?", a: "It's the practice of structuring your content and authority so AI engines (ChatGPT, Perplexity, Google AI Overviews) cite and recommend you — the fastest-growing discovery channel today." },
      { q: "Is AI automation right for a {loc} small business?", a: "Yes — some of the biggest wins come from small businesses that can't afford to miss leads or staff phones 24/7. AI lead response and booking pay for themselves quickly." },
      { q: "Will the AI sound robotic to customers?", a: "No. We train it on your brand voice and verified information, with guardrails and human escalation, so conversations feel natural and helpful." },
      { q: "How long does it take to deploy AI automation?", a: "Most AI automations are live within 2–4 weeks, including integration with your calendar, CRM and support tools." },
    ],
    related: ["seo-agency", "marketing-automation-agency", "digital-marketing-agency"],
  },

  {
    slug: "branding-agency",
    name: "Branding Agency",
    shortName: "Branding",
    emoji: "✨",
    serviceHref: "/web-design",
    heroAccent: "A brand people remember and choose.",
    tagline:
      "Lumotis is a branding agency helping {loc} businesses build distinctive brand identities, messaging and visual systems that stand out in a crowded market — and convert across every channel.",
    stats: [
      { value: "+33%", label: "avg brand-recall lift" },
      { value: "2.4×", label: "avg engagement post-rebrand" },
      { value: "80+", label: "brands built" },
    ],
    pillars: [
      { title: "Brand Strategy & Positioning", body: "Define what makes you different and why {loc} customers should choose you." },
      { title: "Visual Identity", body: "Logo, colour, typography and design systems that look world-class." },
      { title: "Messaging & Voice", body: "Clear, compelling messaging that resonates with your audience." },
      { title: "Brand Guidelines", body: "A complete system so every touchpoint stays consistent." },
      { title: "Website & Digital Application", body: "Your brand brought to life across web, social and ads." },
      { title: "Launch & Rollout", body: "A coordinated launch that makes your new brand land with impact." },
    ],
    faq: [
      { q: "Do I need a rebrand or a refresh?", a: "If your brand no longer reflects your business, audience or ambition, a rebrand may be right. If the foundation is strong but dated, a refresh often delivers the impact at lower cost. We'll advise honestly." },
      { q: "How long does a branding project take?", a: "Most brand identity projects take 6–10 weeks from strategy through final guidelines, depending on scope." },
      { q: "Does branding actually affect revenue?", a: "Significantly. Strong brands convert better, command higher prices, earn more referrals and make every marketing dollar work harder." },
      { q: "Can you also build our website after branding?", a: "Yes — we design and build websites that bring the new brand to life, ensuring consistency from identity to digital experience." },
    ],
    related: ["web-design-agency", "social-media-marketing-agency", "content-marketing-agency"],
  },
];

export const US_KEYWORD_SLUGS = US_KEYWORDS.map((k) => k.slug);

export function getKeywordBySlug(slug) {
  return US_KEYWORDS.find((k) => k.slug === slug) || null;
}

// Fill {loc} / {state} placeholders in a copy string.
export function fillCopy(str, loc) {
  if (!str || !loc) return str || "";
  return str
    .replace(/\{loc\}/g, loc.name)
    .replace(/\{state\}/g, loc.stateName || loc.name);
}
